@let hasShiftUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasClientReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Client);
@let hasCirculationGroupReadPermission =
  can(RoleAction.Read, RolePermissionSubject.CirculationGroup);
@let forbiddenMessage = 'general.forbidden_action' | translate;
@let isCommentPromoted = isPromoted('comment');
@let isPlanningCommentPromoted = isPromoted('planningComment');
@let isMaintenanceNumberPromoted = isPromoted('maintenanceNumber');
@let isSharePlanNumberPromoted = isPromoted('sharePlanNumber');
@let isAttachmentsPromoted = isPromoted('attachments');
@let isShiftFeaturesPromoted = isPromoted('shiftFeatures');
@let isGroupingPromoted = isPromoted('grouping');
@let isSimpleView = isSimpleActivitiesView();
@let activityFieldsDisabled = areActivityFieldsDisabled();

<nz-collapse [nzBordered]="false" nzGhost>
  <nz-collapse-panel
    class="collapse-panel"
    [nzHeader]="'page.simplified_shift_drawer.information_section' | translate"
    [nzActive]="isExpandedSignal()"
    (nzActiveChange)="isExpandedSignal.set($event)"
  >
    <form [formGroup]="form" novalidate>
      <div class="form-field">
        <label for="simplified-shift-name">
          {{ 'page.shifts.shiftname' | translate }}*
        </label>
        <input
          id="simplified-shift-name"
          nz-input
          formControlName="name"
          [placeholder]="'page.shifts.shiftname_placeholder' | translate"
          (focusin)="handleFocus('name')"
          (focusout)="handleBlur()"
        />
      </div>

      <div class="form-field">
        <label>{{ 'general.assignee' | translate }}</label>
        <wilson-users-select
          formControlName="userId"
          [hideLabel]="true"
        ></wilson-users-select>
      </div>

      <div class="form-field">
        <label>{{ 'page.shifts.date' | translate }}*</label>
        <nz-date-picker
          wilsonUseLocalizedDateFormat
          class="w-100"
          formControlName="shiftDate"
          [nzDisabled]="activityFieldsDisabled"
        ></nz-date-picker>
      </div>

      @if (isSimpleView) {
        <div class="form-row">
          <div class="form-field">
            <label>{{ 'general.start.time' | translate }}*</label>
            <nz-time-picker
              wilsonTimeAutoFormat
              class="w-100"
              formControlName="startTime"
              [nzDisabled]="activityFieldsDisabled"
            ></nz-time-picker>
          </div>
          <div class="form-field">
            <label>{{ 'page.activity.end_time' | translate }}*</label>
            <nz-time-picker
              wilsonTimeAutoFormat
              class="w-100"
              formControlName="endTime"
              [nzDisabled]="activityFieldsDisabled"
            ></nz-time-picker>
          </div>
        </div>

        <div class="form-field">
          <label>{{ 'page.simplified_shift_drawer.task' | translate }}*</label>
          <nz-select
            formControlName="activityCategoryId"
            nzShowSearch
            nzAllowClear
            [nzDisabled]="activityFieldsDisabled"
          >
            @for (category of activityCategories$ | async; track category.id) {
              <nz-option
                [nzValue]="category.id"
                [nzLabel]="category | translateActivityCategory"
              ></nz-option>
            }
          </nz-select>
          @if (!activityFieldsDisabled) {
            <button
              type="button"
              class="extended-mode-cta"
              (click)="switchToExtendedActivitiesView()"
            >
              {{
                'page.simplified_shift_drawer.add_more_tasks_extended_mode'
                  | translate
              }}
            </button>
          }
        </div>

        <div class="form-field">
          <label>{{ 'page.simplified_shift_drawer.break' | translate }}</label>
          <div class="break-options">
            @for (minutes of breakDurationOptions; track minutes) {
              <button
                type="button"
                class="break-option"
                [class.break-option--selected]="
                  form.controls.breakOption.value === minutes
                "
                [disabled]="
                  activityFieldsDisabled ||
                  !isBreakDurationOptionEnabled(minutes)
                "
                nz-tooltip
                [nzTooltipTitle]="
                  !activityFieldsDisabled &&
                  !isBreakDurationOptionEnabled(minutes)
                    ? ('page.simplified_shift_drawer.break_option_unavailable'
                      | translate)
                    : ''
                "
                (click)="toggleBreak(minutes)"
              >
                {{ minutes }}m
              </button>
            }
            <button
              type="button"
              class="break-option"
              [class.break-option--selected]="
                form.controls.breakOption.value === 'exact'
              "
              [disabled]="activityFieldsDisabled"
              (click)="toggleBreak('exact')"
            >
              {{ 'page.simplified_shift_drawer.break_exact_time' | translate }}
            </button>
          </div>
        </div>

        @if (form.controls.breakOption.value === 'exact') {
          <div class="form-row exact-break-times">
            <div class="form-field">
              <label>
                {{ 'page.simplified_shift_drawer.break_from' | translate }}
              </label>
              <nz-time-picker
                wilsonTimeAutoFormat
                class="w-100"
                [class.break-time-invalid]="
                  !!getExactBreakErrorTranslationKey()
                "
                formControlName="breakStartTime"
                nzPlaceHolder="--:--"
                [nzDisabled]="activityFieldsDisabled"
              ></nz-time-picker>
            </div>
            <div class="form-field">
              <label>
                {{ 'page.simplified_shift_drawer.break_to' | translate }}
              </label>
              <nz-time-picker
                wilsonTimeAutoFormat
                class="w-100"
                [class.break-time-invalid]="
                  !!getExactBreakErrorTranslationKey()
                "
                formControlName="breakEndTime"
                nzPlaceHolder="--:--"
                [nzDisabled]="activityFieldsDisabled"
              ></nz-time-picker>
            </div>
          </div>
          @if (getExactBreakErrorTranslationKey(); as breakErrorKey) {
            <div class="break-validation-error">
              {{ breakErrorKey | translate }}
            </div>
          }
        }

        <div class="form-field">
          <label>{{ 'general.location' | translate }}*</label>
          <wilson-location-selects
            formControlName="location"
            [sector]="(sectorName$ | async) ?? undefined"
          ></wilson-location-selects>
        </div>
      } @else {
        <div class="extended-activities">
          <wilson-collapsible-activities-list
            [collapsible]="false"
            [isExpandedOnOpen]="true"
            [shift]="shift()"
            (refreshTableDataAndUpdateShiftPublication)="
              refreshActivities.emit()
            "
          >
          </wilson-collapsible-activities-list>
          @if (formService.canReturnToSimpleView() && !activityFieldsDisabled) {
            <button
              type="button"
              class="extended-mode-cta"
              (click)="switchToSimpleActivitiesView()"
            >
              {{
                'page.simplified_shift_drawer.return_to_simple_view' | translate
              }}
            </button>
          }
        </div>
      }

      <div class="form-field">
        <label>{{ 'page.shift.project' | translate }}</label>
        <wilson-set-shift-project
          [projectId]="form.controls.projectId.value ?? ''"
          [projects]="(projects$ | async) ?? []"
          (setProjectId)="setProjectId($event)"
        ></wilson-set-shift-project>
      </div>

      <div class="form-field">
        <label>
          {{ 'page.simplified_shift_drawer.client_agreement' | translate }}
        </label>
        @if (hasClientReadPermission) {
          <wilson-agreement-picker
            [selectedAgreementId]="form.controls.agreementId.value"
            [hasMixedAgreements]="formService.hasMixedAgreements()"
            [mixedAgreementsPlaceholderKey]="
              'page.simplified_shift_drawer.multiple_agreements'
            "
            [isDisabled]="activityFieldsDisabled"
            (selectedAgreement)="setAgreementId($event)"
          ></wilson-agreement-picker>
        } @else {
          <nz-select
            [nzDisabled]="true"
            nz-tooltip
            [nzTooltipTitle]="forbiddenMessage"
            [nzPlaceHolder]="'agreement.select.placeholder' | translate"
          ></nz-select>
        }
      </div>

      <div class="form-field">
        <label>{{ 'page.shifts.labels' | translate }}</label>
        <wilson-select-labels
          [selectedLabels]="form.controls.labels.value"
          [enableNoLabelOption]="false"
          (changedLabels)="setLabels($event)"
        ></wilson-select-labels>
      </div>

      <div class="form-field">
        <label>
          {{ 'page.shifts.shift_category_placeholder' | translate }}*
        </label>
        <wilson-shift-category-select
          formControlName="shiftCategoryId"
        ></wilson-shift-category-select>
      </div>

      <div class="form-field">
        <label>{{ 'page.shifts.orgUnit_placeholder' | translate }}*</label>
        <wilson-organizational-unit-select
          formControlName="organizationalUnitId"
          [placeholder]="'page.shifts.orgUnit' | translate"
        ></wilson-organizational-unit-select>
      </div>

      <ng-container
        [ngTemplateOutlet]="tierTwoFields"
        [ngTemplateOutletContext]="{
          showComment: isCommentPromoted,
          showPlanningComment: isPlanningCommentPromoted,
          showMaintenanceNumber: isMaintenanceNumberPromoted,
          showSharePlanNumber: isSharePlanNumberPromoted,
          showAttachments: isAttachmentsPromoted,
          showShiftFeatures: isShiftFeaturesPromoted,
          showGrouping: isGroupingPromoted,
        }"
      ></ng-container>

      <button
        type="button"
        class="tier-toggle mb-3"
        (click)="showAllTierTwoFields.set(!showAllTierTwoFields())"
      >
        <span class="tier-toggle__line"></span>
        <span class="tier-toggle__label">
          {{
            (showAllTierTwoFields()
              ? 'page.simplified_shift_drawer.show_less'
              : 'page.simplified_shift_drawer.show_more'
            ) | translate
          }}
        </span>
        <span class="tier-toggle__line"></span>
      </button>

      @if (showAllTierTwoFields()) {
        <div class="tier-two">
          <ng-container
            [ngTemplateOutlet]="tierTwoFields"
            [ngTemplateOutletContext]="{
              showComment: !isCommentPromoted,
              showPlanningComment: !isPlanningCommentPromoted,
              showMaintenanceNumber: !isMaintenanceNumberPromoted,
              showSharePlanNumber: !isSharePlanNumberPromoted,
              showAttachments: !isAttachmentsPromoted,
              showShiftFeatures: !isShiftFeaturesPromoted,
              showGrouping: !isGroupingPromoted,
            }"
          ></ng-container>
        </div>
      }

      <ng-template
        #tierTwoFields
        let-showComment="showComment"
        let-showPlanningComment="showPlanningComment"
        let-showMaintenanceNumber="showMaintenanceNumber"
        let-showSharePlanNumber="showSharePlanNumber"
        let-showAttachments="showAttachments"
        let-showShiftFeatures="showShiftFeatures"
        let-showGrouping="showGrouping"
      >
        @if (showComment) {
          <ng-container [ngTemplateOutlet]="commentField"></ng-container>
        }
        @if (showPlanningComment) {
          <ng-container
            [ngTemplateOutlet]="planningCommentField"
          ></ng-container>
        }
        @if (showMaintenanceNumber) {
          <ng-container
            [ngTemplateOutlet]="maintenanceNumberField"
          ></ng-container>
        }
        @if (showSharePlanNumber) {
          <ng-container
            [ngTemplateOutlet]="sharePlanNumberField"
          ></ng-container>
        }
        @if (showAttachments) {
          <ng-container [ngTemplateOutlet]="attachmentsField"></ng-container>
        }
        @if (showShiftFeatures) {
          <ng-container [ngTemplateOutlet]="shiftFeaturesField"></ng-container>
        }
        @if (showGrouping) {
          <ng-container [ngTemplateOutlet]="groupingField"></ng-container>
        }
      </ng-template>

      <ng-template #attachmentsField>
        <wilson-collapsible-shift-attachments
          [collapsible]="false"
          [selectedShift]="shift()"
        ></wilson-collapsible-shift-attachments>
      </ng-template>

      <ng-template #shiftFeaturesField>
        <wilson-collapsible-shift-further-information
          [collapsible]="false"
          [shift]="shift()"
          [currentShiftNecessaryStay]="!!shift().ignoreStayNecessary"
          (updatedNecessaryStay)="updatedNecessaryStay.emit($event)"
          (reloadValidations)="refreshActivities.emit()"
        ></wilson-collapsible-shift-further-information>
      </ng-template>

      <ng-template #groupingField>
        @if (
          hasCirculationGroupReadPermission &&
          ('portal-circulation-grouping' | featureFlagPure | async)
        ) {
          <wilson-collapsible-grouping-section
            [collapsible]="false"
            [drawerItemId]="shift().id"
          ></wilson-collapsible-grouping-section>
        }
      </ng-template>

      <ng-template #commentField>
        <div class="mb-2">
          <label class="form-text d-flex align-items-center">
            {{ 'general.comment' | translate }}
          </label>
          <div
            nz-tooltip
            [nzTooltipTitle]="!hasShiftUpdatePermission ? forbiddenMessage : ''"
            data-test-id="wilson-simplified-shift-drawer:comment-input-wrapper"
          >
            <div
              class="NgxEditor__Wrapper"
              [class.disabled-ngx-editor]="!hasShiftUpdatePermission"
              (focusin)="handleFocus('comment')"
              (focusout)="handleBlur()"
            >
              <div class="editor-container">
                <ngx-editor
                  outputFormat="html"
                  data-test-id="wilson-simplified-shift-drawer:comment-input"
                  formControlName="comment"
                  [editor]="commentEditor"
                  [placeholder]="'page.shifts.comment_placeholder' | translate"
                ></ngx-editor>
              </div>
            </div>
          </div>
        </div>
      </ng-template>

      <ng-template #planningCommentField>
        <div class="mb-2">
          <label
            class="form-text d-flex align-items-center"
            [class.text-danger]="
              form.controls.planningComment | formControlInvalid
            "
          >
            {{ 'page.shift_timeline.shift.planning_comment' | translate }}
            <fa-icon
              [icon]="faInfoCircle"
              class="ms-1"
              nz-tooltip
              [nzTooltipTitle]="
                'page.shift_timeline.shift.planning_comment.tooltip' | translate
              "
            ></fa-icon>
          </label>
          <div
            nz-tooltip
            [nzTooltipTitle]="!hasShiftUpdatePermission ? forbiddenMessage : ''"
            data-test-id="wilson-simplified-shift-drawer:planning-comment-input-wrapper"
          >
            <div
              class="NgxEditor__Wrapper"
              [class.disabled-ngx-editor]="!hasShiftUpdatePermission"
              [class.invalid-comment-input]="
                form.controls.planningComment | formControlInvalid
              "
              (focusin)="handleFocus('planningComment')"
              (focusout)="handleBlur()"
            >
              <div class="editor-container">
                <ngx-editor
                  outputFormat="html"
                  data-test-id="wilson-simplified-shift-drawer:planning-comment-input"
                  formControlName="planningComment"
                  [editor]="planningCommentEditor"
                  [placeholder]="
                    'page.shift_timeline.shift.planning_comment.placeholder'
                      | translate
                  "
                ></ngx-editor>
              </div>
            </div>
          </div>
        </div>
      </ng-template>

      <ng-template #maintenanceNumberField>
        @if (
          hasShiftUpdatePermission &&
          ('billing-db-compliant-fields' | featureFlagPure | async)
        ) {
          <div class="mb-2">
            <div class="form-text d-flex align-items-center">
              {{ 'general.maintenance_number' | translate }}
            </div>
            <div
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftUpdatePermission ? forbiddenMessage : ''
              "
              data-test-id="wilson-simplified-shift-drawer:maintenance-number-input-wrapper"
            >
              <input
                nz-input
                type="text"
                formControlName="maintenanceNumber"
                [maxLength]="50"
                data-test-id="wilson-simplified-shift-drawer:maintenance-number-input"
                [readonly]="!hasShiftUpdatePermission"
                [class.disabled-input]="!hasShiftUpdatePermission"
                (focusin)="handleFocus('maintenanceNumber')"
                (focusout)="handleBlur()"
              />
            </div>
          </div>
        }
      </ng-template>

      <ng-template #sharePlanNumberField>
        @if (
          hasShiftUpdatePermission &&
          ('billing-db-compliant-fields' | featureFlagPure | async)
        ) {
          <div class="mb-2">
            <div class="form-text d-flex align-items-center">
              {{ 'general.shareplan_number' | translate }}
            </div>
            <div
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftUpdatePermission ? forbiddenMessage : ''
              "
              data-test-id="wilson-simplified-shift-drawer:share-plan-number-input-wrapper"
            >
              <input
                nz-input
                type="text"
                formControlName="sharePlanNumber"
                [maxLength]="50"
                data-test-id="wilson-simplified-shift-drawer:share-plan-number-input"
                [readonly]="!hasShiftUpdatePermission"
                [class.disabled-input]="!hasShiftUpdatePermission"
                (focusin)="handleFocus('sharePlanNumber')"
                (focusout)="handleBlur()"
              />
            </div>
          </div>
        }
      </ng-template>
    </form>
  </nz-collapse-panel>
</nz-collapse>
